import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { ArrowRight, Heart, ShoppingBag } from 'lucide-react';
import { Link } from '@/lib/router-compat';
import { Button } from '@/components/ui/button';
import { money } from '@/lib/comparison';
import { dealDonation } from '@/components/deals/DealsPages';
import { Carousel, CarouselContent, CarouselItem, CarouselNext, CarouselPrevious } from '@/components/ui/carousel';
import { HOME_OFFER_CATEGORIES, HOME_OFFERS_MIN_PER_TAB, type HomeOfferCategoryKey } from '@/lib/homeOffers.functions';
import { homeOffersQueryOptions } from '@/lib/homeQueries';
import { cn } from '@/lib/utils';
import type { Deal } from '@/lib/deals.functions';

function HomeOfferCard({ deal }: { deal: Deal }) {
  const [imageFailed, setImageFailed] = useState(false);
  if (imageFailed) return null;
  const donation = dealDonation(deal);
  return (
    <article className="group flex h-full min-w-0 flex-col overflow-hidden rounded-md border bg-card shadow-xs transition-all duration-200 hover:-translate-y-0.5 hover:border-primary/60 hover:shadow-md motion-reduce:transform-none">
      <Link to={`/produto/${deal.slug}`} className="relative flex aspect-[4/3] items-center justify-center overflow-hidden bg-background p-2 sm:p-3">
        <img
          src={deal.image_url}
          alt={deal.title}
          width={420}
          height={315}
          loading="lazy"
          decoding="async"
          className="h-full w-full object-contain transition-transform duration-300 group-hover:scale-[1.03] motion-reduce:transform-none"
          onError={() => setImageFailed(true)}
        />
        <span className="absolute left-2 top-2 rounded-sm bg-destructive px-2 py-1 text-xs font-bold text-destructive-foreground">
          -{deal.discount_percent}%
        </span>
      </Link>
      <div className="flex flex-1 flex-col p-3 sm:p-4">
        <Link to={`/promocoes/${deal.brand.slug}`} className="truncate text-xs font-bold uppercase text-link hover:underline">
          {deal.brand.name}
        </Link>
        <h3 className="mt-1.5 line-clamp-2 min-h-10 text-sm font-semibold leading-5">
          <Link to={`/produto/${deal.slug}`} className="hover:text-link">{deal.title}</Link>
        </h3>
        <div className="mt-3">
          <p className="text-xs text-muted-foreground line-through tabular-nums">{money(deal.original_price)}</p>
          <p className="font-display text-xl font-bold tabular-nums text-foreground sm:text-2xl">{money(deal.price)}</p>
        </div>
        {donation != null && (
          <p className="mt-1 flex items-center gap-1 text-xs font-semibold text-success">
            <Heart className="h-3.5 w-3.5 fill-current" aria-hidden="true" />
            Até {money(donation)} para a causa
          </p>
        )}
        <Button asChild variant="outline" size="sm" className="mt-4 w-full border-primary/50">
          <Link to={`/produto/${deal.slug}`}>Ver oferta <ArrowRight className="h-3.5 w-3.5" /></Link>
        </Button>
      </div>
    </article>
  );
}

export function HomeOffers() {
  const { data } = useQuery(homeOffersQueryOptions());
  const available = HOME_OFFER_CATEGORIES.filter((category) => (data?.[category.key]?.length ?? 0) >= (category.key === 'featured' ? 1 : HOME_OFFERS_MIN_PER_TAB));
  const [active, setActive] = useState<HomeOfferCategoryKey>('featured');
  if (!data || available.length === 0) return null;
  const current = available.find((category) => category.key === active) ?? available[0];
  const offers = data[current.key];
  const target = current.slug ? `/ofertas/${current.slug}` : '/ofertas';

  return (
    <section className="border-y bg-muted/30 py-10 md:py-14" aria-labelledby="home-ofertas-title">
      <div className="container">
        <div className="mb-5 flex flex-wrap items-end justify-between gap-3 md:mb-7">
          <div>
            <p className="mb-2 text-xs font-bold uppercase text-success">Preços conferidos nas lojas</p>
            <h2 id="home-ofertas-title" className="font-display text-2xl font-bold md:text-3xl">Ofertas que valem a pena</h2>
            <p className="mt-2 max-w-2xl text-sm text-muted-foreground md:text-base">Promoções reais, com imagem e uma loja por destaque para você descobrir mais opções.</p>
          </div>
          <Button asChild variant="outline" className="hidden sm:inline-flex"><Link to="/ofertas">Ver todas as ofertas</Link></Button>
        </div>

        <div role="tablist" aria-label="Filtrar ofertas por categoria" className="mb-6 flex gap-2 overflow-x-auto pb-2 scrollbar-hide">
          {available.map((category) => (
            <Button
              key={category.key}
              type="button"
              role="tab"
              aria-selected={category.key === current.key}
              variant={category.key === current.key ? 'default' : 'outline'}
              size="sm"
              className={cn('shrink-0', category.key === current.key && 'shadow-sm')}
              onClick={() => setActive(category.key)}
            >
              {category.name}
            </Button>
          ))}
        </div>

        <Carousel key={current.key} opts={{ align: 'start', dragFree: true }} className="relative">
          <CarouselContent className="-ml-3 md:-ml-4">
            {offers.map((deal) => (
              <CarouselItem key={deal.id} className="basis-1/2 pl-3 sm:basis-1/3 md:pl-4 lg:basis-1/4 xl:basis-1/5">
                <HomeOfferCard deal={deal} />
              </CarouselItem>
            ))}
          </CarouselContent>
          <CarouselPrevious className="-left-3 hidden md:flex" />
          <CarouselNext className="-right-3 hidden md:flex" />
        </Carousel>

        <div className="mt-6 flex flex-wrap items-center justify-between gap-3">
          <Link to={target} className="inline-flex items-center gap-1 text-sm font-bold text-link hover:underline">
            {current.slug ? `Ver mais ofertas de ${current.name}` : 'Explorar todas as ofertas'} <ArrowRight className="h-4 w-4" />
          </Link>
          <Link to="/comparador-de-precos" className="inline-flex items-center gap-1 text-sm font-semibold text-muted-foreground hover:text-link">
            <ShoppingBag className="h-4 w-4" /> Comparar preços
          </Link>
        </div>
      </div>
    </section>
  );
}